/// <amd-module name="Wiki/PageTree/Components/TreeNodeDraggable" />
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import { css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { DragEventsSource } from "Wiki/ViewCommon/Constants";
import { IWikiTransferData } from "Wiki/ViewCommon/Contracts";
import { isSafari } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface TreeNodeDraggableProps<T> extends IVssComponentProperties {
    item: T;
    canDrag: boolean;
    dataTransferValue: string;
    onDrag(source: T): void;
    onDrop(target: T, isReorderOperation?: boolean, isReorderAbove?: boolean): void;
    canDrop(target: T, isReorderOperation?: boolean): boolean;
}

export interface TreeNodeDraggableState {
    isOverReparentTarget?: boolean;
    isOverTopReorderTarget?: boolean;
    isOverBottomReorderTarget?: boolean;
}

const AllowedDragEffect = "all";

export class TreeNodeDraggable<T> extends VssComponent<TreeNodeDraggableProps<T>, TreeNodeDraggableState> {
    private _topReorderTargetId: string | undefined;
    private _bottomReorderTargetId: string | undefined;

    constructor(props: TreeNodeDraggableProps<T>, context: IVssComponentContext) {
        super(props);
        this.state = {};

        const randomDOMId = getId();
        this._topReorderTargetId = "top-reorder-target-" + randomDOMId;
        this._bottomReorderTargetId = "bottom-reorder-target-" + randomDOMId;
    }

    public render(): JSX.Element {
        const dragInProgressClass = "dragging";
        // The reorder-target, though placed after reparent-target, is not below it.
        // Reorder-target will overlap reparent-target and will be placed such that the top of both targets align
        return (
            <div className={css("tree-node-draggable", this.props.className)}>
                <div
                    className={css("reparent-target", this.state.isOverReparentTarget ? dragInProgressClass : "")}
                    draggable={this.props.canDrag}
                    onDragStart={this._onDragStart}
                    onDragEnter={this._onReparentDragEnter}
                    onDragOver={this._onReparentDragEnter}
                    onDragLeave={this._onReparentDragExit}
                    onDrop={this._onReparentDrop}
                >
                    {this.props.children}
                </div>
                <div
                    id={this._topReorderTargetId}
                    className={css("top-target", this.state.isOverTopReorderTarget ? dragInProgressClass : "")}
                    onDragEnter={this._onReorderDragEnter}
                    onDragOver={this._onReorderDragEnter}
                    onDragLeave={this._onReorderDragExit}
                    onDrop={this._onReorderDrop}
                />
                <div
                    id={this._bottomReorderTargetId}
                    className={css("bottom-target", this.state.isOverBottomReorderTarget ? dragInProgressClass : "")}
                    onDragEnter={this._onReorderDragEnter}
                    onDragOver={this._onReorderDragEnter}
                    onDragLeave={this._onReorderDragExit}
                    onDrop={this._onReorderDrop}
                />
            </div>
        );
    }

    // Drag and drop handlers for the reparent and copy link operation
    private _onDragStart = (event: React.DragEvent<HTMLDivElement>): void => {
        if (this.props.canDrag) {
            event.dataTransfer.effectAllowed = AllowedDragEffect;
            this.props.onDrag(this.props.item);
            const wikiTransferObject: IWikiTransferData = {
                source: DragEventsSource.WikiTreeNode,
                text: this.props.dataTransferValue
            };

            // Required for creating link when wiki tree node is dropped on editor
            event.dataTransfer.setData("text", JSON.stringify(wikiTransferObject));
        }
    };

    private _onReparentDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
        if (this._isDragOperationSupported(event)) {
            this._stop(event);
            this.setState({ isOverReparentTarget: true });
        }
    };

    private _onReparentDragExit = (event: React.DragEvent<HTMLDivElement>): void => {
        if (this._isDragOperationSupported(event)) {
            this._stop(event);
            this.setState({ isOverReparentTarget: false });
        }
    };

    private _onReparentDrop = (event: React.DragEvent<HTMLDivElement>): void => {
        if (this._isDragOperationSupported(event)) {
            this._stop(event);
            this.setState({ isOverReparentTarget: false });
            this.props.onDrop(this.props.item);
        }
    };

    // Drag and drop handlers for the reorder operation
    private _onReorderDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
        const isReorderAboveOperation = this._isTopReorderTargetEvent(event);
        if (this._isDragOperationSupported(event, true)) {
            this._stop(event);

            if (isReorderAboveOperation) {
                this.setState({ isOverTopReorderTarget: true });
            } else {
                this.setState({ isOverBottomReorderTarget: true });
            }
        }
    };

    private _onReorderDragExit = (event: React.DragEvent<HTMLDivElement>): void => {
        const isReorderAboveOperation = this._isTopReorderTargetEvent(event);
        if (this._isDragOperationSupported(event, true)) {
            this._stop(event);

            if (isReorderAboveOperation) {
                this.setState({ isOverTopReorderTarget: false });
            } else {
                this.setState({ isOverBottomReorderTarget: false });
            }
        }
    };

    private _onReorderDrop = (event: React.DragEvent<HTMLDivElement>): void => {
        const isReorderAboveOperation = this._isTopReorderTargetEvent(event);
        if (this._isDragOperationSupported(event, true)) {
            this._stop(event);

            if (isReorderAboveOperation) {
                this.setState({ isOverTopReorderTarget: false });
            } else {
                this.setState({ isOverBottomReorderTarget: false });
            }

            this.props.onDrop(this.props.item, true, isReorderAboveOperation);
        }
    };

    private _stop(event: React.DragEvent<HTMLDivElement>): void {
        event.preventDefault();
        event.stopPropagation();
    }

    private _isDragOperationSupported(event: React.DragEvent<HTMLDivElement>, isReorderOperation?: boolean): boolean {
        const dragEffectAllowed = event.dataTransfer.effectAllowed;
        // Safari updates effectAllowed - dont rely on exact values for finding operation supported
        const isDragEffectAllowed = isSafari() ? !!dragEffectAllowed && dragEffectAllowed !== "none" : dragEffectAllowed === AllowedDragEffect;
        const isDragTargetValid = this.props.canDrop(this.props.item, isReorderOperation);

        return isDragEffectAllowed && isDragTargetValid && this.props.canDrag;
    }

    private _isTopReorderTargetEvent(event: React.DragEvent<HTMLDivElement>): boolean {
        const target: HTMLDivElement = event.target as HTMLDivElement;

        return target.id === this._topReorderTargetId;
    }
}
